/**
 * CuratorAccreditationPage — регистрация и аккредитация участников.
 * Куратор может найти компанию/представителя по имени или QR,
 * отметить прибытие, распечатать бейдж на месте.
 */
import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Search, UserCheck, QrCode, Download, CheckCircle2,
  Clock, X, ArrowLeft, Building2, User, Printer, Badge,
  RefreshCw, MapPin, Users, Loader2,
} from "lucide-react";
import type { CompanySummary } from "@mvk/shared";
import { useAdminEvent } from "../../hooks/useAdminEvent";
import { tryLoadExhibitors } from "../../../services/adminEventsApi";
import { catalogService } from "../../../services/catalogService";
import type { Company, Representative } from "../../../types";

function summaryToCompany(summary: CompanySummary, eventId: string): Company {
  return {
    id: summary.id,
    eventId,
    name: summary.name,
    category: summary.category,
    tags: [],
    hall: summary.hall ?? "",
    booth: summary.booth ?? "",
    shortAbout: summary.shortAbout,
    products: [],
    contact: {},
    qrCodeValue: `company:${summary.id}`,
    mapPoint: { x: 0, y: 0, hallId: "" },
  };
}

type CheckInStatus = "arrived" | "pending";

export function CuratorAccreditationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { event, loading: eventLoading } = useAdminEvent(id);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "arrived" | "pending">("all");
  const [arrivedIds, setArrivedIds] = useState<Set<string>>(new Set());
  const [companies, setCompanies] = useState<Company[]>([]);
  const [repsByCompany, setRepsByCompany] = useState<Record<string, Representative[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await tryLoadExhibitors(id);
        const mapped = (list ?? []).map((s) => summaryToCompany(s, id));
        if (cancelled) return;
        setCompanies(mapped);
        const entries = await Promise.all(
          mapped.map(async (c) => {
            try {
              const reps = await catalogService.listRepresentatives(id, c.id, "admin");
              return [c.id, reps] as const;
            } catch {
              return [c.id, [] as Representative[]] as const;
            }
          }),
        );
        if (!cancelled) setRepsByCompany(Object.fromEntries(entries));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const rows = useMemo(
    () =>
      companies.map((c) => ({
        company: c,
        reps: repsByCompany[c.id] ?? [],
        arrived: arrivedIds.has(c.id),
      })),
    [companies, repsByCompany, arrivedIds],
  );

  const filtered = useMemo(() => rows.filter(({ company, arrived }) => {
    const matchSearch = company.name.toLowerCase().includes(search.toLowerCase()) ||
      company.booth.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || (filter === "arrived" ? arrived : !arrived);
    return matchSearch && matchFilter;
  }), [rows, search, filter]);

  const arrivedCount = rows.filter((r) => r.arrived).length;

  const toggleArrived = (companyId: string) => {
    const s = new Set(arrivedIds);
    s.has(companyId) ? s.delete(companyId) : s.add(companyId);
    setArrivedIds(s);
  };

  if (eventLoading || loading) {
    return (
      <div className="p-6 flex justify-center text-gray-400">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  if (!event) return <div className="p-6 text-gray-500">Мероприятие не найдено</div>;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(`/events/${id}`)}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hidden sm:block">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-[19px] text-gray-900">Аккредитация</h1>
          <p className="text-[12px] text-gray-400 mt-0.5">{event.title}</p>
        </div>
        <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-[13px] hover:border-indigo-200 transition-colors">
          <RefreshCw size={14} /> Обновить
        </button>
      </div>

      {/* Progress */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[14px] text-gray-900">Прибыло экспонентов</div>
            <div className="text-[12px] text-gray-400 mt-0.5">{arrivedCount} из {companies.length}</div>
          </div>
          <div className="text-[32px] text-gray-900 leading-none">
            {companies.length > 0 ? Math.round((arrivedCount / companies.length) * 100) : 0}%
          </div>
        </div>
        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 rounded-full transition-all duration-500"
            style={{ width: `${companies.length > 0 ? (arrivedCount / companies.length) * 100 : 0}%` }}
          />
        </div>
        <div className="flex items-center gap-4 mt-3 text-[12px] text-gray-500">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Прибыли: {arrivedCount}</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gray-300" /> Ожидаем: {companies.length - arrivedCount}</span>
        </div>
      </div>

      {/* Filter + search */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          {([["all", "Все"], ["arrived", "Прибыли"], ["pending", "Ожидаем"]] as const).map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)}
              className={`px-3 py-1.5 rounded-lg text-[12px] transition-colors ${
                filter === v ? "bg-indigo-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-indigo-200"
              }`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2">
          <Search size={14} className="text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Компания, стенд, QR-код..."
            className="flex-1 text-[13px] outline-none text-gray-700 placeholder-gray-400" />
          {search && <button onClick={() => setSearch("")}><X size={14} className="text-gray-400" /></button>}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {/* Desktop header */}
        <div className="hidden sm:grid grid-cols-[2fr_1fr_1fr_auto] gap-4 px-4 py-2.5 border-b border-gray-50 text-[11px] text-gray-400 uppercase tracking-wider">
          <div>Компания</div>
          <div>Стенд</div>
          <div>Представители</div>
          <div>Статус</div>
        </div>

        <div className="divide-y divide-gray-50">
          {filtered.map(({ company, reps, arrived }) => (
            <div key={company.id} className="px-4 py-3 hover:bg-gray-50/50 transition-colors">
              {/* Desktop row */}
              <div className="hidden sm:grid grid-cols-[2fr_1fr_1fr_auto] gap-4 items-center">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-[13px] text-indigo-600 flex-shrink-0">
                    {company.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] text-gray-900 truncate">{company.name}</div>
                    <div className="text-[11px] text-gray-400 truncate">{company.category}</div>
                  </div>
                </div>
                <div>
                  <div className="inline-flex items-center gap-1 text-[12px] text-gray-700 bg-gray-100 px-2 py-0.5 rounded-md">
                    <MapPin size={10} className="text-gray-400" /> {company.booth}
                  </div>
                </div>
                <div className="text-[12px] text-gray-600">
                  {reps.length > 0 ? (
                    <span className="flex items-center gap-1"><Users size={12} className="text-gray-400" />{reps.length} чел.</span>
                  ) : (
                    <span className="text-amber-500 text-[11px]">Нет бейджей</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {arrived ? (
                    <div className="flex items-center gap-1.5 text-[12px] text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
                      <CheckCircle2 size={12} /> Прибыл
                    </div>
                  ) : (
                    <button onClick={() => toggleArrived(company.id)}
                      className="flex items-center gap-1.5 text-[12px] text-gray-500 bg-gray-100 hover:bg-emerald-50 hover:text-emerald-600 px-2.5 py-1 rounded-full transition-colors">
                      <UserCheck size={12} /> Отметить
                    </button>
                  )}
                  <button className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-500 transition-colors" title="Печать бейджа">
                    <Printer size={14} />
                  </button>
                </div>
              </div>

              {/* Mobile card */}
              <div className="sm:hidden">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-[15px] text-indigo-600 flex-shrink-0">
                    {company.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-[14px] text-gray-900">{company.name}</div>
                        <div className="text-[11px] text-gray-400">{company.booth} · {reps.length} представителей</div>
                      </div>
                      {arrived ? (
                        <span className="flex items-center gap-1 text-[11px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex-shrink-0">
                          <CheckCircle2 size={10} /> Прибыл
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[11px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                          <Clock size={10} /> Ожидаем
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 mt-2">
                      {!arrived && (
                        <button onClick={() => toggleArrived(company.id)}
                          className="flex-1 py-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[12px] flex items-center justify-center gap-1.5 transition-colors">
                          <UserCheck size={13} /> Отметить прибытие
                        </button>
                      )}
                      {arrived && (
                        <button onClick={() => toggleArrived(company.id)}
                          className="flex-1 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-500 text-[12px] flex items-center justify-center gap-1.5 transition-colors">
                          <X size={13} /> Отменить
                        </button>
                      )}
                      <button className="px-3 py-2 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-[12px] flex items-center gap-1.5 transition-colors">
                        <Printer size={13} /> Бейдж
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* QR scan hint */}
      <div className="bg-indigo-50 rounded-xl border border-indigo-100 p-4 flex items-start gap-3">
        <QrCode size={20} className="text-indigo-400 flex-shrink-0 mt-0.5" />
        <div>
          <div className="text-[13px] text-indigo-800">Сканирование QR-кода</div>
          <div className="text-[12px] text-indigo-600 mt-0.5">
            Направьте камеру на QR-код стенда или бейдж представителя — компания будет найдена автоматически и отмечена как прибывшая.
          </div>
        </div>
      </div>
    </div>
  );
}
